import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import type { CompletionStats } from '../../types';
import {
  CHART_ERROR,
  CHART_LABEL_LINE_STYLE,
  CHART_LEGEND_STYLE,
  CHART_NEUTRAL,
  CHART_SUCCESS,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_STYLE,
} from './chartTheme';
import { ChartEmptyState, ChartPanel, ChartSliceGradient } from './ChartPrimitives';
import { chartGradientFill, useChartGradientId } from './chartGradient';

interface CompletionChartProps {
  stats: CompletionStats;
  title?: string;
}

const COLORS = {
  completed: CHART_SUCCESS,
  failed: CHART_ERROR,
  cancelled: CHART_NEUTRAL,
};

interface ChartDataItem {
  name: string;
  value: number;
  color: string;
  percent: number;
}

export function CompletionChart({ stats, title = 'Completion Rate' }: CompletionChartProps) {
  const gradientBaseId = useChartGradientId('completion-slice');

  // Pie chart percentages are relative to total (for correct visual proportions)
  // Success rate (completedPercent) excludes cancelled scans
  const data: ChartDataItem[] = [
    { name: 'Completed', value: stats.completed, color: COLORS.completed, percent: stats.total > 0 ? (stats.completed / stats.total) * 100 : 0 },
    { name: 'Failed', value: stats.failed, color: COLORS.failed, percent: stats.total > 0 ? (stats.failed / stats.total) * 100 : 0 },
    { name: 'Cancelled', value: stats.cancelled, color: COLORS.cancelled, percent: stats.cancelledPercent },
  ].filter(d => d.value > 0);

  const hasData = stats.total > 0;

  return (
    <ChartPanel
      title={title}
      iconClassName="bg-primary-50 text-primary-600"
      icon={
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      }
      summary={
        <div className="flex items-center gap-3">
          <div className="text-center">
            <p className="text-lg font-semibold leading-none" style={{ color: CHART_SUCCESS }}>
              {stats.completedPercent.toFixed(1)}%
            </p>
            <p className="mt-1 text-[10px] uppercase text-slate-500">Success</p>
          </div>
          <div className="h-7 w-px bg-slate-200" />
          <div className="text-center">
            <p className="text-lg font-semibold leading-none text-slate-700">{stats.total}</p>
            <p className="mt-1 text-[10px] uppercase text-slate-500">Total</p>
          </div>
        </div>
      }
    >

      {/* Pie Chart */}
      {hasData ? (
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <defs>
                {data.map((entry, index) => (
                  <ChartSliceGradient
                    key={entry.name}
                    id={`${gradientBaseId}-${index}`}
                    color={entry.color}
                  />
                ))}
              </defs>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius="38%"
                outerRadius="62%"
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) => `${name}: ${percent.toFixed(1)}%`}
                labelLine={CHART_LABEL_LINE_STYLE}
                isAnimationActive={false}
              >
                {data.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={chartGradientFill(`${gradientBaseId}-${index}`)}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE}
                labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                formatter={(value: number, name: string) => [`${value} scans`, name]}
              />
              <Legend
                verticalAlign="top"
                height={24}
                wrapperStyle={CHART_LEGEND_STYLE}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <ChartEmptyState>No scan data available</ChartEmptyState>
      )}

      {/* Status breakdown */}
      <div className="mt-2 grid flex-shrink-0 grid-cols-3 gap-2 border-t border-slate-200 pt-3">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: CHART_SUCCESS }} />
            <span className="text-xs font-medium text-slate-700">{stats.completed}</span>
          </div>
          <p className="text-[10px] text-slate-500">Completed</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: CHART_ERROR }} />
            <span className="text-xs font-medium text-slate-700">{stats.failed}</span>
          </div>
          <p className="text-[10px] text-slate-500">Failed</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1">
            <div className="w-2 h-2 rounded-full bg-surface-400" />
            <span className="text-xs font-medium text-slate-700">{stats.cancelled}</span>
          </div>
          <p className="text-[10px] text-slate-500">Cancelled</p>
        </div>
      </div>
    </ChartPanel>
  );
}
